/**
 * Unarmatured flesh — the mesh no bone reaches (ADR-031 §3.2, T1).
 *
 * ONE responsibility: given a skinned mesh in bind-world and the bones that
 * are supposed to own it, find the regions that sit FAR from every bone
 * segment, cluster them by connectivity, and say what shape each cluster is —
 * a TUBE (a chain candidate: a tail, a braid), a SHEET (a cloth candidate: a
 * coat panel, a cape), or a BLOB (nothing to propose). It proposes; it never
 * decides. The tail zone it finds is confirmed or corrected on the flyout.
 *
 * WHY DISTANCE TO A SEGMENT AND NOT TO A JOINT. A vertex halfway down a long
 * femur is far from both joints and perfectly armatured; the bone is the
 * segment between them. Measured on the tiger warrior (Tripo HD, 12,748 verts,
 * 40 segments): the median vertex sits 6.2 cm from its nearest segment, the
 * 90th percentile 11.5 cm — and the tail, which Tripo skinned to the right
 * thigh because it has no tail bones, sits a mean 16.2 cm from ANY bone across
 * 375 vertices with elongation 10.6 (memory `tripo-hd-segmentation-cues`).
 * Flagging above 2× the median and clustering by mesh connectivity returned
 * that tail, the two coat-back panels (sheets, weighted to the calves) and the
 * mane crown (a blob on the short Head bone) — and nothing on a limb.
 *
 * THE THRESHOLD IS A DECLARATION, NOT A CONSTANT. 2× median found the tail
 * cleanly; 3× lost most of it. It is a knob with a measured default (D8), and
 * the caller records which value a run used.
 *
 * THE KIND TEST IS v1-CRUDE, AND SAYS SO. Extents of the cluster's bounding
 * box, sorted: `flatness = smallest / middle`, `elongation = largest /
 * smallest`. A sheet is thin in ONE direction; a tube is long relative to its
 * thickness and not thin. A tightly CURLED tail can bound like a sheet — the
 * flyout's "this is a tail" override is the human backstop (ADR-031 §9.3),
 * and a medial-path-based girth is the successor when the corpus asks.
 *
 * Kernel-pure: typed arrays in, plain data out. Depends on: nothing.
 * Depended on by: the app's zone proposer (T1) and the tiger specimen probe.
 */

export class UnarmaturedRefusal extends Error {}

export interface Point3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** A bone as the detector needs it: a name, a parent name, and where it sits at bind, in the same space as the vertices. */
export interface DetectorBone {
  readonly name: string;
  readonly parent: string | null;
  readonly worldPosition: Point3;
}

export interface UnarmaturedInput {
  /** Bind-world vertex positions, xyz interleaved — `position · bindMatrix`, the hand pipeline's definition. */
  readonly positions: Float32Array;
  /** Triangle list, three indices per face. */
  readonly indices: Uint32Array | Uint16Array;
  readonly bones: readonly DetectorBone[];
  /** Skin attributes, four influences per vertex — only for the dominance report; absent or null means no report. */
  readonly skin?: {
    readonly index: Uint16Array | Uint8Array | Uint32Array;
    readonly weight: Float32Array;
  } | null;
}

export interface UnarmaturedOptions {
  /** Flag a vertex whose nearest-segment distance exceeds this many medians. Default 2 (measured on the tiger). */
  readonly thresholdMultiplier?: number;
  /** Clusters smaller than this are noise, not proposals. Default 30. */
  readonly minClusterVertices?: number;
  /**
   * After clustering, grow each cluster by this many RINGS of neighbouring
   * vertices over the mesh, regardless of distance. Default 0 (the raw
   * measurement). WHY RINGS AND NOT A LOWER THRESHOLD: the seed threshold
   * correctly refuses the flesh nearest the bones, which is exactly where a
   * tail JOINS the body — and a lower threshold floods there (the tiger: a
   * 1.6× hysteresis grew the tail by 700 vertices into the coat and buttocks,
   * while rings grew it by 20, 43, 64, 89 for one to four rings and reached
   * the junction at two). A ring is the wand's own unit (ADR-031 §3.2), so the
   * flyout's wider / tighter is ±1 here.
   */
  readonly growRings?: number;
}

export type ClusterKind = 'tube' | 'sheet' | 'blob';

export interface UnarmaturedCluster {
  /** Mesh vertex indices (all of them, including position-welded twins). */
  readonly vertexIndices: Uint32Array;
  /** Faces whose three corners all belong to this cluster — a Zone's face set. */
  readonly faceIndices: Uint32Array;
  readonly centre: Point3;
  /** Bounding-box extents, largest first. */
  readonly extents: readonly [number, number, number];
  /** largest / smallest extent. */
  readonly elongation: number;
  /** smallest / middle extent — near zero for a sheet. */
  readonly flatness: number;
  readonly kind: ClusterKind;
  /** Mean distance of the cluster's vertices from their nearest bone segment. */
  readonly meanDistance: number;
  /** Which bones currently own this flesh, by dominant weight — the evidence a tail "swings with the right leg". */
  readonly dominantBones: readonly { readonly bone: string; readonly share: number }[];
}

export interface UnarmaturedReport {
  readonly vertexCount: number;
  readonly segmentCount: number;
  readonly medianDistance: number;
  readonly thresholdMultiplier: number;
  readonly threshold: number;
  readonly growRings: number;
  readonly flaggedVertices: number;
  /** Largest first. */
  readonly clusters: readonly UnarmaturedCluster[];
}

const DEFAULT_THRESHOLD_MULTIPLIER = 2;
const DEFAULT_MIN_CLUSTER = 30;
/** smallest/middle below this reads as one thin direction: a sheet. */
const SHEET_FLATNESS = 0.25;
/** largest/smallest at or above this, and not a sheet, reads as a tube. */
const TUBE_ELONGATION = 4;

interface Segment {
  readonly ax: number;
  readonly ay: number;
  readonly az: number;
  readonly bx: number;
  readonly by: number;
  readonly bz: number;
}

/** Parent→child segments for every bone whose parent is in the set. */
function boneSegments(bones: readonly DetectorBone[]): Segment[] {
  const byName = new Map(bones.map((b) => [b.name, b]));
  const segments: Segment[] = [];
  for (const bone of bones) {
    if (bone.parent === null) continue;
    const parent = byName.get(bone.parent);
    if (parent === undefined) continue;
    segments.push({
      ax: parent.worldPosition.x,
      ay: parent.worldPosition.y,
      az: parent.worldPosition.z,
      bx: bone.worldPosition.x,
      by: bone.worldPosition.y,
      bz: bone.worldPosition.z,
    });
  }
  return segments;
}

function distanceToSegment(px: number, py: number, pz: number, s: Segment): number {
  const abx = s.bx - s.ax;
  const aby = s.by - s.ay;
  const abz = s.bz - s.az;
  const apx = px - s.ax;
  const apy = py - s.ay;
  const apz = pz - s.az;
  const l2 = abx * abx + aby * aby + abz * abz;
  let t = l2 > 0 ? (apx * abx + apy * aby + apz * abz) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const dx = px - (s.ax + abx * t);
  const dy = py - (s.ay + aby * t);
  const dz = pz - (s.az + abz * t);
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function classify(extents: readonly [number, number, number]): { kind: ClusterKind; elongation: number; flatness: number } {
  const [a, b, c] = extents;
  const eps = 1e-9;
  const flatness = c / Math.max(b, eps);
  const elongation = a / Math.max(c, eps);
  if (flatness < SHEET_FLATNESS) return { kind: 'sheet', elongation, flatness };
  if (elongation >= TUBE_ELONGATION) return { kind: 'tube', elongation, flatness };
  return { kind: 'blob', elongation, flatness };
}

/**
 * Every vertex's distance to its nearest parent→child bone segment — the
 * detector's one measurement, exported so the tail detector seeds from the
 * SAME number (Float32, bind-world units). Refuses a rig with no segment.
 */
export function nearestSegmentDistances(positions: Float32Array, bones: readonly DetectorBone[]): Float32Array {
  const segments = boneSegments(bones);
  if (segments.length === 0) {
    throw new UnarmaturedRefusal(
      'needs a rig with at least one parent→child bone segment to know what no bone reaches — this mesh has none',
    );
  }
  const n = positions.length / 3;
  const distance = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const px = positions[i * 3]!;
    const py = positions[i * 3 + 1]!;
    const pz = positions[i * 3 + 2]!;
    let best = Infinity;
    for (const s of segments) {
      const d = distanceToSegment(px, py, pz, s);
      if (d < best) best = d;
    }
    distance[i] = best;
  }
  return distance;
}

export function detectUnarmatured(input: UnarmaturedInput, options: UnarmaturedOptions = {}): UnarmaturedReport {
  const multiplier = options.thresholdMultiplier ?? DEFAULT_THRESHOLD_MULTIPLIER;
  const minCluster = options.minClusterVertices ?? DEFAULT_MIN_CLUSTER;
  const growRings = options.growRings ?? 0;
  if (!(multiplier > 0)) throw new UnarmaturedRefusal(`threshold multiplier ${multiplier} is not positive`);
  if (!Number.isInteger(growRings) || growRings < 0 || growRings > 32) {
    throw new UnarmaturedRefusal(`growRings ${growRings} is not a whole number of rings in 0..32`);
  }
  const n = input.positions.length / 3;
  if (n === 0 || input.indices.length < 3) throw new UnarmaturedRefusal('the mesh has no triangles — nothing to measure');
  const segments = boneSegments(input.bones);
  if (segments.length === 0) {
    throw new UnarmaturedRefusal(
      'needs a rig with at least one parent→child bone segment to know what no bone reaches — this mesh has none',
    );
  }

  // ---- distance from every vertex to its nearest segment ----
  const distance = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const px = input.positions[i * 3]!;
    const py = input.positions[i * 3 + 1]!;
    const pz = input.positions[i * 3 + 2]!;
    let best = Infinity;
    for (const s of segments) {
      const d = distanceToSegment(px, py, pz, s);
      if (d < best) best = d;
    }
    distance[i] = best;
  }
  const sorted = Float32Array.from(distance).sort();
  const median = sorted[Math.floor(n / 2)]!;
  const threshold = median * multiplier;

  // ---- flag, then cluster the flagged flesh by mesh connectivity ----
  // Welded by exact position so a UV or normal seam never splits one tail in
  // two (the tiger: 12,748 verts, 9,719 positions).
  const flagged = new Uint8Array(n);
  let flaggedCount = 0;
  for (let i = 0; i < n; i++) {
    if (distance[i]! > threshold) {
      flagged[i] = 1;
      flaggedCount++;
    }
  }
  const rep = new Int32Array(n);
  const byKey = new Map<string, number>();
  for (let i = 0; i < n; i++) {
    const k = `${input.positions[i * 3]},${input.positions[i * 3 + 1]},${input.positions[i * 3 + 2]}`;
    let r = byKey.get(k);
    if (r === undefined) {
      r = i;
      byKey.set(k, i);
    }
    rep[i] = r;
  }
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (a: number): number => {
    while (parent[a] !== a) {
      parent[a] = parent[parent[a]!]!;
      a = parent[a]!;
    }
    return a;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  const faceCount = Math.floor(input.indices.length / 3);
  for (let f = 0; f < faceCount; f++) {
    const a = rep[input.indices[f * 3]!]!;
    const b = rep[input.indices[f * 3 + 1]!]!;
    const c = rep[input.indices[f * 3 + 2]!]!;
    if (flagged[a] && flagged[b]) union(a, b);
    if (flagged[b] && flagged[c]) union(b, c);
    if (flagged[c] && flagged[a]) union(c, a);
  }
  // Cluster membership is decided on WELDED representatives, then every twin
  // of a member rides along — a seam twin whose own distance rounds under the
  // threshold still belongs with its position.
  const twinsOf = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = rep[i]!;
    let list = twinsOf.get(r);
    if (list === undefined) {
      list = [];
      twinsOf.set(r, list);
    }
    list.push(i);
  }
  const repMembers = new Map<number, Set<number>>();
  for (const r of twinsOf.keys()) {
    if (!flagged[r]) continue;
    const root = find(r);
    let set = repMembers.get(root);
    if (set === undefined) {
      set = new Set();
      repMembers.set(root, set);
    }
    set.add(r);
  }
  // ---- grow each cluster by rings over the welded adjacency (see growRings) ----
  if (growRings > 0) {
    const adjacency = new Map<number, Set<number>>();
    const link = (a: number, b: number): void => {
      let s = adjacency.get(a);
      if (s === undefined) {
        s = new Set();
        adjacency.set(a, s);
      }
      s.add(b);
    };
    for (let f = 0; f < faceCount; f++) {
      const a = rep[input.indices[f * 3]!]!;
      const b = rep[input.indices[f * 3 + 1]!]!;
      const c = rep[input.indices[f * 3 + 2]!]!;
      link(a, b);
      link(b, a);
      link(b, c);
      link(c, b);
      link(c, a);
      link(a, c);
    }
    for (const set of repMembers.values()) {
      let frontier = [...set];
      for (let ring = 0; ring < growRings && frontier.length > 0; ring++) {
        const next: number[] = [];
        for (const r of frontier) {
          for (const nb of adjacency.get(r) ?? []) {
            if (!set.has(nb)) {
              set.add(nb);
              next.push(nb);
            }
          }
        }
        frontier = next;
      }
    }
  }
  const members = new Map<number, number[]>();
  for (const [root, set] of repMembers) {
    const list: number[] = [];
    for (const r of set) for (const twin of twinsOf.get(r) ?? []) list.push(twin);
    list.sort((a, b) => a - b);
    members.set(root, list);
  }

  // ---- describe each cluster ----
  const clusters: UnarmaturedCluster[] = [];
  for (const [root, verts] of members) {
    if (verts.length < minCluster) continue;
    const inCluster = new Uint8Array(n);
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    let dSum = 0;
    const dominance = new Map<number, number>();
    for (const i of verts) {
      inCluster[i] = 1;
      const x = input.positions[i * 3]!;
      const y = input.positions[i * 3 + 1]!;
      const z = input.positions[i * 3 + 2]!;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      if (z > maxZ) maxZ = z;
      dSum += distance[i]!;
      if (input.skin) {
        let bestW = -1;
        let bestB = -1;
        for (let k = 0; k < 4; k++) {
          const w = input.skin.weight[i * 4 + k]!;
          if (w > bestW) {
            bestW = w;
            bestB = input.skin.index[i * 4 + k]!;
          }
        }
        // A vertex whose four weights are all zero is owned by nobody — its
        // padding slot (commonly bone 0) must not be counted (Codex P2, #136;
        // the hand-isolation path's own rule).
        if (bestW > 0 && bestB >= 0) dominance.set(bestB, (dominance.get(bestB) ?? 0) + 1);
      }
    }
    const faces: number[] = [];
    for (let f = 0; f < faceCount; f++) {
      if (
        inCluster[input.indices[f * 3]!] &&
        inCluster[input.indices[f * 3 + 1]!] &&
        inCluster[input.indices[f * 3 + 2]!]
      ) {
        faces.push(f);
      }
    }
    const extents = [maxX - minX, maxY - minY, maxZ - minZ].sort((p, q) => q - p) as [number, number, number];
    const { kind, elongation, flatness } = classify(extents);
    const dominantBones = [...dominance.entries()]
      .sort((p, q) => q[1] - p[1])
      .slice(0, 3)
      .map(([bone, count]) => ({ bone: input.bones[bone]?.name ?? `#${bone}`, share: count / verts.length }));
    void root;
    clusters.push({
      vertexIndices: Uint32Array.from(verts),
      faceIndices: Uint32Array.from(faces),
      centre: { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: (minZ + maxZ) / 2 },
      extents,
      elongation,
      flatness,
      kind,
      meanDistance: dSum / verts.length,
      dominantBones,
    });
  }
  clusters.sort((p, q) => q.vertexIndices.length - p.vertexIndices.length);

  return {
    vertexCount: n,
    segmentCount: segments.length,
    medianDistance: median,
    thresholdMultiplier: multiplier,
    threshold,
    growRings,
    flaggedVertices: flaggedCount,
    clusters,
  };
}
